// Ads Health — the PRD's six sub-features.
//
// One list of findings, one score on top. The six sub-features are six families
// of checks writing into that one list; the score summarises it, and the list is
// what the seller works through.
//
// Diagnostic only. Nothing here changes a bid, a budget, a keyword, a campaign
// or a listing. Every finding names the tool that can fix it, and nothing is
// applied without the seller's approval.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiArrowLeft, FiDownloadCloud, FiFileText,
         FiLink } from 'react-icons/fi';

import shell from '../SellerIntelligence.module.css';
import s from './AdsHealth.module.css';
import AuthContext from '../../../context/AuthContext';
import FeatureCards from './components/FeatureCards';
import Findings from './components/Findings';
import Score from './components/Score';
import Waste from './components/Waste';
import Audit from './components/Audit';
import Gaps from './components/Gaps';
import Readiness from './components/Readiness';
import Diagnosis from './components/Diagnosis';
import { NotConnectedError, collect, dismissFinding, getAudit, getAuditPdf,
         getDiagnosis, getGaps, getOverview, getReadiness, getScore, getWaste,
         restoreFinding } from './api';

const TABS = [
  { id: 'findings', label: 'What to fix', ref: '' },
  { id: 'health', label: 'Health score', ref: 'SF1' },
  { id: 'waste', label: 'Wasted spend', ref: 'SF2' },
  { id: 'audit', label: 'Structure', ref: 'SF3' },
  { id: 'gaps', label: 'Coverage gaps', ref: 'SF4' },
  { id: 'readiness', label: 'Readiness', ref: 'SF5' },
  { id: 'diagnosis', label: 'Listing diagnosis', ref: 'SF6' },
];
const RANGES = [[7, 'Last 7 days'], [14, 'Last 14 days'], [30, 'Last 30 days'],
                [60, 'Last 60 days']];

export default function AdsHealth() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [tab, setTab] = useState('findings');
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [notConnected, setNotConnected] = useState(null);
  const [reload, setReload] = useState(0);

  const fail = useCallback((e) => {
    if (e instanceof NotConnectedError) setNotConnected(e.message);
    else setError(e.message);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let live = true;
    setLoading(true);
    getOverview(token, { days })
      .then((r) => { if (live) { setOverview(r); setNotConnected(null); setError(null); } })
      .catch((e) => { if (live) fail(e); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [token, days, fail, reload]);

  const load = useCallback(async () => {
    if (!token || tab === 'findings' || tab === 'health') return;
    setLoading(true); setError(null);
    try {
      if (tab === 'waste') setView(await getWaste(token, { days }));
      else if (tab === 'audit') setView(await getAudit(token, { days }));
      else if (tab === 'gaps') setView(await getGaps(token, { days }));
      else if (tab === 'readiness') setView(await getReadiness(token, { days }));
      else if (tab === 'diagnosis') setView(await getDiagnosis(token, { days }));
    } catch (e) { fail(e); } finally { setLoading(false); }
    // `reload` is here on purpose: dismissing or restoring a finding must
    // re-fetch the view, and it is the only thing that changes when it does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tab, days, fail, reload]);

  useEffect(() => { load(); }, [load]);

  const [score, setScore] = useState(null);
  useEffect(() => {
    if (!token || tab !== 'health') return;
    getScore(token, { days }).then(setScore).catch(fail);
  }, [token, tab, days, fail, reload]);

  const onDismiss = async (finding) => {
    setBusy(true);
    try {
      const r = await dismissFinding(token, finding.id);
      setNotice(r.returns_if_impact_reaches
        ? `Hidden. It comes back if it reaches ₹${Number(r.returns_if_impact_reaches).toLocaleString('en-IN')}.`
        : 'Hidden.');
      setReload((n) => n + 1);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const onRestore = async (id) => {
    setBusy(true);
    try { await restoreFinding(token, id); setReload((n) => n + 1); }
    catch (e) { fail(e); } finally { setBusy(false); }
  };

  const onAudit = async () => {
    setBusy(true); setNotice(null);
    try {
      const blob = await getAuditPdf(token, { days });
      // Handed to the browser as a blob so the download carries the bearer
      // token the route needs; a plain link cannot.
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ads-health-audit-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setNotice('Audit downloaded.');
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const onCollect = async () => {
    setBusy(true); setNotice(null);
    try {
      const r = await collect(token, { days });
      const parts = [];
      if (r.eligibility?.asked) parts.push(`${r.eligibility.asked} products checked for eligibility`);
      if (r.budget?.recorded) parts.push(`${r.budget.recorded} campaign budgets recorded`);
      if (r.stock?.products) parts.push(`${r.stock.products} listings read`);
      if (r.prices?.recorded) parts.push(`${r.prices.recorded} prices snapshotted`);
      setNotice(parts.length ? parts.join(' · ') : 'Nothing new to collect.');
      setReload((n) => n + 1);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  function body() {
    if (notConnected) {
      return (
        <div className={s.locked}>
          <div className={s.lockedIcon}><FiLink /></div>
          <h2 className={s.lockedTitle}>Amazon Ads is not connected</h2>
          <p className={s.lockedBody}>
            {notConnected} Advertising is a second Amazon authorisation, separate from the
            Selling Partner one — connect it on the Amazon card and collect the last 60
            days. Nothing here is shown as a score of zero in the meantime.
          </p>
          <button className={s.button} onClick={() => navigate('/dashboard/market-places')}>
            Go to the Amazon card
          </button>
        </div>
      );
    }

    return (
      <div className={s.page}>
        {error && <div className={s.error}>{error}</div>}
        {notice && <div className={s.notice}>{notice}</div>}

        <section className={s.panel}>
          <div className={s.contextBar}>
            <div className={s.contextLeft}>
              <label className={s.field}>
                <span className={s.label}>Period</span>
                <select className={s.select} value={days}
                        onChange={(e) => setDays(Number(e.target.value))}>
                  {RANGES.map(([n, label]) => <option key={n} value={n}>{label}</option>)}
                </select>
              </label>
              <button className={s.linkButton} onClick={onCollect} disabled={busy}>
                <FiDownloadCloud /> Refresh stock, prices and eligibility
              </button>
              <button className={s.linkButton} onClick={onAudit} disabled={busy}>
                <FiFileText /> Download audit (PDF)
              </button>
            </div>
            {overview && (
              <div className={s.coverage}>
                {overview.window?.excluded_note}
                {' '}Target ACOS {(overview.target_acos * 100).toFixed(0)}%
                {overview.target_source === 'benchmark'
                  ? ' (a benchmark, not a target you set)' : ''}.
              </div>
            )}
          </div>
          {overview?.vendor_note && (
            <div className={s.coverageWarn}>{overview.vendor_note}</div>
          )}
          {overview?.freshness?.stale && (
            <div className={s.coverageWarn}>
              <FiAlertTriangle />{' '}
              {overview.freshness.why} No new finding is being raised until the next sync.
            </div>
          )}
        </section>

        <FeatureCards data={overview} loading={loading && !overview}
                      onOpen={(next) => setTab(next)} />

        <section className={s.panel}>
          <div className={s.tabs}>
            {TABS.map((t) => (
              <button key={t.id} className={tab === t.id ? s.tabActive : s.tab}
                      onClick={() => setTab(t.id)}>
                {t.label}{t.ref && <span className={s.tabRef}>{t.ref}</span>}
              </button>
            ))}
          </div>

          <div style={{ paddingTop: 16 }}>
            {tab === 'findings' && (
              <Findings data={overview} currency={overview?.currency}
                        onDismiss={onDismiss} onRestore={onRestore} busy={busy} />
            )}
            {tab === 'health' && <Score data={score} currency={overview?.currency} />}
            {tab === 'waste' && (
              <Waste data={view} currency={overview?.currency}
                     findings={view?.findings} />
            )}
            {tab === 'audit' && (
              <Audit data={view} currency={overview?.currency} findings={view?.findings} />
            )}
            {tab === 'gaps' && (
              <Gaps data={view} currency={overview?.currency} findings={view?.findings} />
            )}
            {tab === 'readiness' && <Readiness data={view} currency={overview?.currency} />}
            {tab === 'diagnosis' && <Diagnosis data={view} currency={overview?.currency} />}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={shell.featureContainer}>
      <div className={shell.header}>
        <button className={shell.backButton}
                onClick={() => navigate('/dashboard/seller-intelligence')}>
          <FiArrowLeft /> Back
        </button>
        <div className={shell.titleSection}>
          <div className={shell.iconTitle}><h1>Ads Health</h1></div>
          <p>What is wrong with your ads, in rupees, worst first</p>
        </div>
      </div>
      <div className={shell.content}>{body()}</div>
    </div>
  );
}
